import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { Kpi, Section, Empty } from "./ui";
import { money, num } from "./format";

// Fixed governorate centres (approximate capital coordinates) — never inferred from sales data.
const GOV_COORDS = {
  "دمشق": [33.5138, 36.2765], "ريف دمشق": [33.35, 36.55], "حلب": [36.2021, 37.1343], "حمص": [34.7324, 36.7137], "حماة": [35.1318, 36.7578],
  "اللاذقية": [35.5317, 35.7915], "طرطوس": [34.889, 35.8866], "إدلب": [35.9306, 36.6339], "دير الزور": [35.3359, 40.1408], "الرقة": [35.9594, 39.0079],
  "الحسكة": [36.5024, 40.7477], "درعا": [32.6189, 36.1021], "السويداء": [32.7094, 36.5695], "القنيطرة": [33.1256, 35.8244],
};

const SyriaMap = ({ cities }) => {
  const ref = useRef(null);
  const mapRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!document.getElementById("leaflet-css")) {
        const link = document.createElement("link");
        link.id = "leaflet-css"; link.rel = "stylesheet"; link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
        document.head.appendChild(link);
      }
      const L = (await import("leaflet")).default;
      if (cancelled || !ref.current) return;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
      const map = L.map(ref.current, { scrollWheelZoom: false, attributionControl: false }).setView([35.0, 38.3], 6);
      mapRef.current = map;
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 12 }).addTo(map);
      const max = Math.max(1, ...cities.map((c) => c.amount));
      cities.forEach((c) => {
        const ll = GOV_COORDS[c.city];
        if (!ll) return;
        const r = 10 + Math.sqrt(c.amount / max) * 30;
        L.circleMarker(ll, { radius: r, color: "#09637E", weight: 2, fillColor: "#088395", fillOpacity: 0.55 }).addTo(map)
          .bindTooltip(`<b>${c.city}</b><br/>${money(c.amount)} · ${c.sales_count} عملية`, { direction: "top" });
      });
    })();
    return () => { cancelled = true; if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, [cities]);
  return <div ref={ref} className="h-64 md:h-80 w-full rounded-xl overflow-hidden border border-[#DCEBEE]" data-testid="geo-map" />;
};

export default function GeoTab({ data }) {
  const [openCity, setOpenCity] = useState(null);
  const mapped = data.cities.filter((c) => GOV_COORDS[c.city]);
  return (
    <div className="space-y-4 md:space-y-6" data-testid="geo-tab">
      <div className="grid grid-cols-3 gap-3 md:gap-4">
        <Kpi label="محافظات لها مبيعات" value={num(data.cities.length)} testid="geo-cities" />
        <Kpi label="الأعلى مبيعاً" value={data.cities[0]?.city || "—"} tone="teal" testid="geo-top" sub={data.cities[0] ? money(data.cities[0].amount) : undefined} />
        <Kpi label="مبيعات بلا موقع مسجّل" value={money(data.unknown.amount)} tone="gray" testid="geo-unknown" sub={`${num(data.unknown.sales_count)} عملية`} />
      </div>
      <Section title="ترتيب المحافظات والمناطق" icon={MapPin} testid="geo-ranking">
        {data.cities.length === 0 ? <Empty text="لا توجد مبيعات بموقع مسجّل ضمن الفترة — يُسجَّل الموقع من حقل المحافظة/المنطقة في البيع الفيزيائي أو من محافظة التاجر المشتري أونلاين" testid="geo-empty" /> : (
          <div className="divide-y divide-[#EBF4F6]">
            {data.cities.map((c, i) => {
              const share = data.total ? Math.round((c.amount / data.total) * 100) : 0;
              const open = openCity === c.city;
              return (
                <div key={c.city} className="py-3" data-testid={`geo-city-${i}`}>
                  <button onClick={() => setOpenCity(open ? null : c.city)} className="w-full flex items-center gap-3 text-right">
                    <span className="w-7 h-7 rounded-full bg-[#EBF4F6] text-[#09637E] font-extrabold text-sm flex items-center justify-center shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-extrabold text-[#333333]">{c.city}</span>
                        <span className="text-lg md:text-xl font-extrabold text-[#09637E] tabular-nums" dir="ltr">{money(c.amount)}</span>
                      </div>
                      <div className="h-2 rounded-full bg-[#EBF4F6] mt-1.5 overflow-hidden"><div className="h-full bg-[#088395]" style={{ width: `${share}%` }} /></div>
                      <div className="flex justify-between text-[11px] text-[#777777] mt-1">
                        <span>{num(c.sales_count)} عملية · {share}%</span>
                        <span>{c.profit === null ? "ربح: غير مكتمل" : <>ربح: <b className={c.profit >= 0 ? "text-emerald-700" : "text-rose-600"} dir="ltr">{money(c.profit)}</b></>}</span>
                      </div>
                    </div>
                  </button>
                  {open && (
                    <div className="mt-2 mr-10 grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid={`geo-areas-${i}`}>
                      {c.areas.map((a) => (
                        <div key={a.area} className="flex items-center justify-between bg-[#F5FAFB] rounded-lg px-3 py-2 text-sm">
                          <span className="font-bold text-[#333333]">{c.city} ← {a.area}</span>
                          <span className="tabular-nums" dir="ltr"><b className="text-[#09637E]">{money(a.amount)}</b> <span className="text-[#888888] text-xs">· {a.sales_count}</span></span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>
      {mapped.length > 0 && (
        <Section title="توزّع المبيعات على الخريطة" icon={MapPin} testid="geo-map-section">
          <SyriaMap cities={mapped} />
          <div className="text-[11px] text-[#888888] mt-2">حجم الدائرة يعبّر عن قيمة المبيعات في المحافظة (مواقع المحافظات ثابتة تقريبية).</div>
        </Section>
      )}
    </div>
  );
}
